import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function SignupDeadlineModal({ show, setShow, signupUntil }) {
  const { t } = useTranslation();

  // Format the signup deadline time for display
  const formatDeadlineTime = (signupUntil) => {
    if (!signupUntil) return "23:00";

    const signupTime = dayjs(signupUntil).tz("Europe/Amsterdam");
    return signupTime.format("HH:mm");
  };

  const deadlineTime = formatDeadlineTime(signupUntil);

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog
        as="div"
        className="z-[10001] fixed inset-0 overflow-y-auto"
        onClose={() => setShow(false)}
      >
        <div className="flex justify-center items-center sm:p-0 px-4 pt-4 pb-20 h-[100vh] text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="z-0 fixed bg-[#00000080] w-full h-screen" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:h-screen sm:align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block relative bg-neutral-200 shadow-xl sm:my-8 sm:p-6 px-4 pt-5 pb-4 rounded-2xl w-[500px] max-w-[90vw] overflow-hidden text-left sm:align-middle align-bottom transition-all transform">
              <div className="top-0 right-0 absolute pt-4 pr-4">
                <button
                  type="button"
                  className="rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 text-neutral-800 hover:text-neutral-700"
                  onClick={() => setShow(false)}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="w-12 h-12" aria-hidden="true" />
                </button>
              </div>
              <div className="flex justify-center mb-3 w-full">
                <div className="flex flex-col items-center w-full">
                  <div className="w-full text-center">
                    <h1 className="w-full font-bold text-[24px] text-black leading-[28px]">
                      {t("signup_deadline_passed") || "Aanmelding Gesloten"}
                    </h1>
                  </div>
                </div>
              </div>

              <hr className="mt-[20px] max-lg:mt-[15px] border-neutral-600/50" />

              <div className="mt-[12px] text-[16px] text-neutral-700 text-center">
                <p className="mb-4">
                  {t("signup_deadline_message") || "Je kunt je alleen aanmelden voor"} <strong>{deadlineTime}</strong> {t("on_event_day") || "op de dag van het evenement"}.
                </p>
                <p>
                  {t("signup_deadline_next_time") || "Volgende keer ben je er hopelijk wat sneller bij! 😉"}
                </p>
              </div>

              <button
                onClick={() => setShow(false)}
                className="disabled:bg-[#56466B] enabled:bg-gradient-to-r enabled:from-[#6A1BD1] enabled:to-[#7721E5] shadow-md mt-[20px] px-[20px] py-[14px] rounded-[8px] w-full font-semibold text-[16px] text-white hover:text-white !disabled:cursor-not-allowed btn"
              >
                {t("understood") || "Begrepen"}
              </button>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}